import {
  ArrowRightOutlined,
  PlayCircleFilled,
  SafetyCertificateOutlined,
  ScanOutlined,
  TeamOutlined,
  WarningOutlined,
} from "@ant-design/icons";
import { Collapse } from "antd";
import { type ReactNode, useState } from "react";
import { useIsMobileView } from "../../hooks/ui-hook";
import { EventType, trackEvent } from "../../utils/analytics";
import styles from "./ComplianceLandingPage.module.scss";

const { Panel } = Collapse;

// Tally form used app-wide for "Contact us" (see AuthLayout.contactUs). Reused
// here so compliance trial requests land in the same inbox the team watches.
const CONTACT_FORM_URL = "https://tally.so/r/wg5QxK";

// Product walkthrough. The embed id is fixed by the design handoff; swap it here
// when a newer cut is published.
const DEMO_VIDEO_ID = "uRaQfABOu1c";
const VIDEO_SECTION_ID = "compliance-landing-video";

type StatItem = { value: string; label: string };
const STATS: StatItem[] = [
  { value: "100%", label: "of notes reviewed" },
  { value: "Same-day", label: "gap detection" },
  { value: "Per-clinician", label: "compliance trends" },
  { value: "Days", label: "to go live" },
];

type ValueProp = { icon: ReactNode; title: string; body: string };
const VALUE_PROPS: ValueProp[] = [
  {
    icon: <ScanOutlined />,
    title: "Every note, reviewed automatically",
    body: "The moment a clinician generates a note, Nudge checks it against your rules — required elements, risk language, medical necessity. No spot-checks. No sampling. 100% of notes, every time.",
  },
  {
    icon: <SafetyCertificateOutlined />,
    title: "Catch gaps before auditors do",
    body: "Surface missing risk assessments, unsupported codes, and documentation gaps while they're still fixable — not after a payer claws back a claim or an audit lands on your desk.",
  },
  {
    icon: <WarningOutlined />,
    title: "Lower your risk-language exposure",
    body: "Flag the suicidal-ideation, safety-planning, and medical-necessity gaps that turn into malpractice and liability later. Get them in front of the right clinician the same day.",
  },
  {
    icon: <TeamOutlined />,
    title: "Clinician-level visibility",
    body: "See compliance trends for every clinician in your group. Spot who needs coaching, track improvement over time, and walk into audits with the receipts.",
  },
];

type Severity = "high" | "med" | "low";
type FindingRow = { initials: string; name: string; color: string; finding: string; meta: string; severity: Severity };
// Illustrative sample rows for the hero product mock (per the design handoff).
// Mirrors the real Findings inbox shape (clinician_name + rationale + judgment).
const SAMPLE_FINDINGS: FindingRow[] = [
  {
    initials: "RL",
    name: "Dr. Rachel Lim",
    color: "#6366f1",
    finding: "Missing risk assessment",
    meta: "SOAP · 2m ago",
    severity: "high",
  },
  {
    initials: "JC",
    name: "Dr. Jamie Cortez",
    color: "#0ea5e9",
    finding: "Medical necessity not documented",
    meta: "Intake · 14m ago",
    severity: "high",
  },
  {
    initials: "MH",
    name: "Dr. Marcus Howell",
    color: "#8b5cf6",
    finding: "Treatment plan goals incomplete",
    meta: "Progress · 1h ago",
    severity: "med",
  },
  {
    initials: "PI",
    name: "Dr. Priya Iyer",
    color: "#ec4899",
    finding: "CPT code unsupported by note",
    meta: "Psych · 2h ago",
    severity: "med",
  },
  {
    initials: "RL",
    name: "Dr. Rachel Lim",
    color: "#6366f1",
    finding: "All required elements present",
    meta: "DAP · 3h ago",
    severity: "low",
  },
];

const SEVERITY_LABEL: Record<Severity, string> = { high: "High", med: "Medium", low: "Pass" };

type FaqItem = { question: string; answer: string };
const FAQ_DATA: FaqItem[] = [
  {
    question: "What does Nudge Compliance actually do?",
    answer:
      "It's an always-on documentation reviewer for your group. As clinicians generate notes in Nudge, the AI evaluates each one against the compliance rules you define and flags anything that falls short — missing elements, risk language, medical necessity, unsupported codes. Findings land in a triage inbox with severity, owner, and SLA tracking so nothing slips through.",
  },
  {
    question: "Is there a free trial?",
    answer:
      "Yes — a free trial is available upon request. Reach out using the form on this page and we'll set your group up with compliance rules tailored to your specialty, so you can see real findings on real notes before you commit.",
  },
  {
    question: "Can I customize the rules to my practice?",
    answer:
      "Completely. Rules are configured per note template, so you decide what every SOAP, intake, DAP, or psychiatric note must document. You control the severity of each rule, who owns the findings, and how quickly they need to be resolved. We'll help you build your starting rule set during onboarding.",
  },
  {
    question: "Who can see the compliance findings?",
    answer:
      "Compliance is admin-controlled. Group owners and designated admins get the findings inbox and per-clinician dashboards. It's built for the people accountable for the group's documentation quality and audit readiness.",
  },
  {
    question: "How long does setup take?",
    answer:
      "Most groups are up and running in days, not weeks. There's nothing to install — it runs on the notes your clinicians already create in Nudge. Request the trial and we'll handle rule configuration with you.",
  },
];

const SeverityBadge = ({ severity }: { severity: Severity }) => (
  <span className={`${styles.landingPage__sev} ${styles[`landingPage__sev_${severity}`]}`}>
    <span className={styles.landingPage__sev_dot} />
    {SEVERITY_LABEL[severity]}
  </span>
);

// Hero product mock — the Findings inbox, made tangible. Static sample data.
const FindingsInboxMock = () => (
  <div className={styles.landingPage__mock_card}>
    <div className={styles.landingPage__mock_header}>
      <div className={styles.landingPage__mock_header_left}>
        <span className={styles.landingPage__mock_icon}>
          <ScanOutlined />
        </span>
        <span className={styles.landingPage__mock_title}>Findings inbox</span>
      </div>
      <span className={styles.landingPage__mock_live}>
        <span className={styles.landingPage__mock_live_dot} />
        Live
      </span>
    </div>
    <div className={styles.landingPage__mock_rows}>
      <div className={styles.landingPage__mock_scan} aria-hidden="true" />
      {SAMPLE_FINDINGS.map((row, i) => (
        <div className={styles.landingPage__mock_row} key={`${row.initials}-${i}`}>
          <span className={styles.landingPage__mock_avatar} style={{ background: row.color }}>
            {row.initials}
          </span>
          <div className={styles.landingPage__mock_row_main}>
            <div className={styles.landingPage__mock_finding}>{row.finding}</div>
            <div className={styles.landingPage__mock_subline}>
              {row.name} · {row.meta}
            </div>
          </div>
          <SeverityBadge severity={row.severity} />
        </div>
      ))}
    </div>
  </div>
);

const ComplianceLandingPage = () => {
  const isMobile = useIsMobileView();
  const [videoPlaying, setVideoPlaying] = useState(false);

  const handleContact = () => {
    trackEvent(EventType.CONTACT_US, { source: "compliance_landing" });
    window.open(CONTACT_FORM_URL, "_blank");
  };

  const scrollToVideo = () => {
    const el = document.getElementById(VIDEO_SECTION_ID);
    if (el) {
      window.scrollTo({ top: el.getBoundingClientRect().top + window.scrollY - 24, behavior: "smooth" });
    }
  };

  return (
    <div className={styles.landingPage}>
      {/* ── Hero (dark editorial) ── */}
      <div className={styles.landingPage__hero_band}>
        <div className={styles.landingPage__container}>
          <section className={styles.landingPage__hero}>
            <div className={styles.landingPage__hero_grid}>
              <div className={styles.landingPage__hero_copy}>
                <span className={styles.landingPage__hero_pill}>
                  <WarningOutlined />
                  Catch it before the payer does
                </span>
                <h1 className={styles.landingPage__hero_title}>
                  One note can sink a claim.
                  <br />
                  <span className={styles.landingPage__hero_title_accent}>We check every single one.</span>
                </h1>
                <p className={styles.landingPage__hero_subcopy}>
                  Nudge Compliance reads every note your clinicians write the instant it's generated — flagging risk
                  language, missing elements, and unsupported codes before they become clawbacks or audit findings.
                </p>
                <div className={styles.landingPage__hero_actions}>
                  <button type="button" className={styles.landingPage__btn_onDark} onClick={handleContact}>
                    Talk to our team
                    <ArrowRightOutlined />
                  </button>
                  <button type="button" className={styles.landingPage__btn_demo} onClick={scrollToVideo}>
                    <PlayCircleFilled />
                    Watch the demo
                  </button>
                </div>
                <p className={styles.landingPage__hero_finecopy}>
                  Trial available upon request · HIPAA-compliant · Built for behavioral health
                </p>
              </div>
              {!isMobile && (
                <div className={styles.landingPage__hero_mock}>
                  <FindingsInboxMock />
                </div>
              )}
            </div>
          </section>

          {/* Stat strip — still inside the dark band */}
          <div className={styles.landingPage__statstrip}>
            {STATS.map((stat) => (
              <div className={styles.landingPage__stat} key={stat.label}>
                <div className={styles.landingPage__stat_value}>{stat.value}</div>
                <div className={styles.landingPage__stat_label}>{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* ── Value props ── */}
      <section className={styles.landingPage__values}>
        <div className={styles.landingPage__container}>
          <div className={styles.landingPage__section_head}>
            <div className={styles.landingPage__eyebrow}>Why groups choose Nudge Compliance</div>
            <h2 className={styles.landingPage__section_title}>The safety net under every chart your group writes</h2>
            <p className={styles.landingPage__section_sub}>
              For the clinic admins, group owners, and clinicians accountable for documentation that holds up to
              scrutiny.
            </p>
          </div>

          <div className={styles.landingPage__vp_grid}>
            {VALUE_PROPS.map((vp) => (
              <div className={styles.landingPage__vp_card} key={vp.title}>
                <span className={styles.landingPage__vp_icon}>{vp.icon}</span>
                <h3 className={styles.landingPage__vp_title}>{vp.title}</h3>
                <p className={styles.landingPage__vp_body}>{vp.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Video ── */}
      <section id={VIDEO_SECTION_ID} className={styles.landingPage__video_section}>
        <div className={styles.landingPage__container}>
          <div className={styles.landingPage__section_head}>
            <div className={styles.landingPage__eyebrow}>See it in action</div>
            <h2 className={styles.landingPage__section_title}>Watch the 2-minute walkthrough</h2>
            <p className={styles.landingPage__section_sub}>
              How a documentation gap goes from flagged to fixed — before it ever reaches a payer.
            </p>
          </div>

          <div className={styles.landingPage__video_card}>
            {videoPlaying ? (
              <iframe
                src={`https://www.youtube.com/embed/${DEMO_VIDEO_ID}?autoplay=1&rel=0`}
                title="Nudge Compliance walkthrough"
                frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                referrerPolicy="strict-origin-when-cross-origin"
                allowFullScreen
                className={styles.landingPage__video_iframe}
              />
            ) : (
              <button
                type="button"
                aria-label="Play walkthrough video"
                className={styles.landingPage__video_facade}
                style={{ backgroundImage: `url(https://i.ytimg.com/vi/${DEMO_VIDEO_ID}/maxresdefault.jpg)` }}
                onClick={() => setVideoPlaying(true)}
              >
                <span className={styles.landingPage__video_scrim} />
                <span className={styles.landingPage__video_play}>
                  <PlayCircleFilled />
                </span>
              </button>
            )}
          </div>

          <div className={styles.landingPage__cta_center}>
            <button type="button" className={styles.primary_cta} onClick={handleContact}>
              Talk to our team
              <ArrowRightOutlined />
            </button>
          </div>
        </div>
      </section>

      {/* ── FAQ ── */}
      <section className={styles.landingPage__faq}>
        <div className={styles.landingPage__container}>
          <div className={styles.landingPage__section_head}>
            <div className={styles.landingPage__eyebrow}>FAQs</div>
            <h2 className={styles.landingPage__section_title}>Curious? Learn more.</h2>
          </div>

          <div className={styles.landingPage__faq_content}>
            <Collapse
              accordion
              ghost
              defaultActiveKey={["0"]}
              expandIconPosition="end"
              expandIcon={({ isActive }) => (
                <span
                  className={styles.landingPage__faq_plus}
                  style={{ transform: isActive ? "rotate(45deg)" : "none" }}
                >
                  +
                </span>
              )}
              className={styles.landingPage__faq_collapse}
            >
              {FAQ_DATA.map((faq, i) => (
                <Panel
                  header={<span className={styles.landingPage__faq_question}>{faq.question}</span>}
                  key={String(i)}
                  className={styles.landingPage__faq_panel}
                >
                  <p className={styles.landingPage__faq_answer}>{faq.answer}</p>
                </Panel>
              ))}
            </Collapse>

            <div className={styles.landingPage__cta_center}>
              <button type="button" className={styles.primary_cta} onClick={handleContact}>
                Talk to our team
                <ArrowRightOutlined />
              </button>
              <p className={styles.landingPage__cta_finecopy}>
                Trial available upon request · We'll tailor the rules to your specialty.
              </p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ComplianceLandingPage;
